import { getRouteApi } from "@tanstack/react-router"
import { useStore } from "zustand"
import { createStore } from "zustand/vanilla"

type PendingSortOption = "submitted_desc" | "submitted_asc" | "member"
type ActiveSortOption = "range_asc" | "range_desc" | "member"

type ReviewMessage = {
  tone: "success" | "error"
  text: string
}

type AbsenceReviewUiState = {
  // Search (HIGH CHURN — every keystroke)
  searchQuery: string

  // Queue filter (click churn)
  queueFilter: "all" | "pending" | "approved"

  // Sort preferences (persisted to localStorage)
  pendingSort: PendingSortOption
  activeSort: ActiveSortOption

  // Selection (checkbox churn)
  selectedPendingIds: Set<string>

  // Keyboard navigation
  focusedPendingIndex: number

  // Mutation feedback
  message: ReviewMessage | null

  setSearchQuery: (value: string) => void
  setQueueFilter: (filter: "all" | "pending" | "approved") => void
  setPendingSort: (sort: PendingSortOption) => void
  setActiveSort: (sort: ActiveSortOption) => void
  toggleRequestId: (requestId: string, checked: boolean) => void
  setVisibleSelected: (ids: string[], checked: boolean) => void
  pruneStaleIds: (validIds: ReadonlySet<string>) => void
  setFocusedPendingIndex: (index: number | ((prev: number) => number)) => void
  setMessage: (message: ReviewMessage | null) => void
  dispose: () => void
}

export const createAbsenceReviewUiStore = () =>
  createStore<AbsenceReviewUiState>((set) => ({
    searchQuery: "",
    queueFilter: "all",
    pendingSort: "submitted_desc",
    activeSort: "range_asc",
    selectedPendingIds: new Set<string>(),
    focusedPendingIndex: 0,
    message: null,

    setSearchQuery: (value) => set({ searchQuery: value }),
    setQueueFilter: (filter) => set({ queueFilter: filter }),
    setPendingSort: (sort) => set({ pendingSort: sort }),
    setActiveSort: (sort) => set({ activeSort: sort }),
    toggleRequestId: (requestId, checked) =>
      set((state) => {
        const next = new Set(state.selectedPendingIds)
        if (checked) {
          next.add(requestId)
        } else {
          next.delete(requestId)
        }
        return { selectedPendingIds: next }
      }),
    setVisibleSelected: (ids, checked) =>
      set(() => ({
        selectedPendingIds: checked ? new Set(ids) : new Set<string>(),
      })),
    pruneStaleIds: (validIds) =>
      set((state) => {
        const next = new Set(
          Array.from(state.selectedPendingIds).filter((id) => validIds.has(id)),
        )
        return next.size === state.selectedPendingIds.size
          ? state
          : { selectedPendingIds: next }
      }),
    setFocusedPendingIndex: (index) =>
      set((state) => ({
        focusedPendingIndex:
          typeof index === "function"
            ? index(state.focusedPendingIndex)
            : index,
      })),
    setMessage: (message) => set({ message }),
    dispose: () => {
      set({
        searchQuery: "",
        queueFilter: "all",
        selectedPendingIds: new Set(),
        focusedPendingIndex: 0,
        message: null,
      })
    },
  }))

export type AbsenceReviewUiStore = ReturnType<typeof createAbsenceReviewUiStore>

// Selector hooks
export const useReviewSearchQuery = (store: AbsenceReviewUiStore) =>
  useStore(store, (s) => s.searchQuery)

export const useReviewQueueFilter = (store: AbsenceReviewUiStore) =>
  useStore(store, (s) => s.queueFilter)

export const useReviewPendingSort = (store: AbsenceReviewUiStore) =>
  useStore(store, (s) => s.pendingSort)

export const useReviewActiveSort = (store: AbsenceReviewUiStore) =>
  useStore(store, (s) => s.activeSort)

export const useReviewSelectedPendingIds = (store: AbsenceReviewUiStore) =>
  useStore(store, (s) => s.selectedPendingIds)

export const useReviewFocusedPendingIndex = (store: AbsenceReviewUiStore) =>
  useStore(store, (s) => s.focusedPendingIndex)

export const useReviewMessage = (store: AbsenceReviewUiStore) =>
  useStore(store, (s) => s.message)

// Route-scoped access
const absenceReviewRouteApi = getRouteApi("/_authed/_admin/absence-review")

export const useAbsenceReviewRouteStore = () =>
  absenceReviewRouteApi.useRouteContext({
    select: (ctx) => ctx.absenceReviewStore,
  })

export type { PendingSortOption, ActiveSortOption, ReviewMessage }
